import jsPDF from 'jspdf';
import html2canvas from 'html2canvas';

export interface CertificateData {
  studentName: string;
  courseName: string;
  completionDate: string;
  instructorName: string;
  certificateId: string;
  logoUrl?: string;
}

export const generateCertificate = async (data: CertificateData): Promise<void> => {
  // Create a temporary div for the certificate
  const certificateDiv = document.createElement('div');
  certificateDiv.id = 'certificate-template';
  certificateDiv.style.position = 'absolute';
  certificateDiv.style.left = '-9999px';
  certificateDiv.style.top = '-9999px';
  certificateDiv.style.width = '1200px';
  certificateDiv.style.height = '800px';
  certificateDiv.style.background = 'white';
  certificateDiv.style.fontFamily = "'Georgia', 'Garamond', serif";
  certificateDiv.style.display = 'flex';
  certificateDiv.style.flexDirection = 'column';
  certificateDiv.style.padding = '0';

  certificateDiv.innerHTML = `
    <div style="
      width: 100%;
      height: 100%;
      background: linear-gradient(135deg, #ffffff 0%, #f5f0ed 50%, #ffffff 100%);
      position: relative;
      font-family: 'Georgia', 'Garamond', serif;
      display: flex;
      flex-direction: column;
      padding: 0;
      box-sizing: border-box;
      overflow: hidden;
    ">
      <!-- Decorative gradient background shapes -->
      <div style="
        position: absolute;
        top: -200px;
        right: -200px;
        width: 500px;
        height: 500px;
        background: radial-gradient(circle, rgba(220, 38, 38, 0.08) 0%, transparent 70%);
        border-radius: 50%;
      "></div>
      
      <div style="
        position: absolute;
        bottom: -150px;
        left: -150px;
        width: 400px;
        height: 400px;
        background: radial-gradient(circle, rgba(139, 92, 246, 0.08) 0%, transparent 70%);
        border-radius: 50%;
      "></div>

      <!-- Outer decorative border frame -->
      <div style="
        position: absolute;
        top: 30px;
        left: 30px;
        right: 30px;
        bottom: 30px;
        border: 3px solid #d4a574;
        border-radius: 12px;
        pointer-events: none;
      "></div>

      <!-- Inner decorative border -->
      <div style="
        position: absolute;
        top: 40px;
        left: 40px;
        right: 40px;
        bottom: 40px;
        border: 1px solid #d4a574;
        border-radius: 8px;
        pointer-events: none;
      "></div>

      <!-- Certificate ID Top Right -->
      <div style="
        position: absolute;
        top: 55px;
        right: 70px;
        z-index: 20;
        text-align: right;
      ">
        <div style="
          font-size: 11pt;
          color: #6b7280;
          font-weight: 600;
          letter-spacing: 1px;
        ">
          CERT ID
        </div>
        <div style="
          font-size: 9pt;
          color: #4b5563;
          font-family: 'Courier New', monospace;
          margin-top: 3px;
          word-break: break-all;
          max-width: 180px;
        ">
          ${data.certificateId}
        </div>
      </div>

      <!-- Corner decorations -->
      <div style="
        position: absolute;
        top: 50px;
        left: 50px;
        width: 40px;
        height: 40px;
        border-top: 3px solid #dc2626;
        border-left: 3px solid #dc2626;
        border-radius: 6px;
      "></div>
      <div style="
        position: absolute;
        top: 50px;
        right: 50px;
        width: 40px;
        height: 40px;
        border-top: 3px solid #dc2626;
        border-right: 3px solid #dc2626;
        border-radius: 6px;
      "></div>
      <div style="
        position: absolute;
        bottom: 50px;
        left: 50px;
        width: 40px;
        height: 40px;
        border-bottom: 3px solid #dc2626;
        border-left: 3px solid #dc2626;
        border-radius: 6px;
      "></div>
      <div style="
        position: absolute;
        bottom: 50px;
        right: 50px;
        width: 40px;
        height: 40px;
        border-bottom: 3px solid #dc2626;
        border-right: 3px solid #dc2626;
        border-radius: 6px;
      "></div>

      <!-- Main content -->
      <div style="
        flex: 1;
        display: flex;
        flex-direction: column;
        padding: 80px 100px;
        position: relative;
        z-index: 10;
        text-align: center;
      ">
        <!-- Logo and Academy name -->
        <div style="margin-bottom: 30px;">
          <img src="${data.logoUrl || '/logo.jpeg'}" alt="Academy Logo" style="
            width: 70px;
            height: 70px;
            border-radius: 50%;
            margin: 0 auto 15px;
            display: block;
            box-shadow: 0 8px 24px rgba(220, 38, 38, 0.2);
            object-fit: contain;
            background: white;
            padding: 5px;
          " />
          <div style="
            font-size: 28pt;
            font-weight: 700;
            color: #1f2937;
            letter-spacing: 2px;
            margin-bottom: 5px;
          ">
            SUDHA'S ACADEMY
          </div>
          <div style="
            width: 200px;
            height: 2px;
            background: linear-gradient(90deg, transparent, #dc2626, transparent);
            margin: 0 auto 10px;
          "></div>
          <div style="
            font-size: 14pt;
            color: #6b7280;
            letter-spacing: 3px;
            text-transform: uppercase;
            font-weight: 500;
          ">
            Certificate of Achievement
          </div>
        </div>

        <!-- Main certificate text -->
        <div style="flex: 1; display: flex; flex-direction: column; justify-content: center; margin-bottom: 30px;">
          <div style="
            font-size: 16pt;
            color: #4b5563;
            margin-bottom: 25px;
            font-style: italic;
            letter-spacing: 1px;
          ">
            This certificate proudly presented to
          </div>

          <!-- Student name -->
          <div style="
            font-size: 48pt;
            font-weight: 700;
            color: #dc2626;
            margin-bottom: 35px;
            letter-spacing: 2px;
            text-transform: uppercase;
          ">
            ${data.studentName}
          </div>

          <div style="
            width: 400px;
            height: 2px;
            background: #dc2626;
            margin: 0 auto 35px;
          "></div>

          <div style="
            font-size: 15pt;
            color: #4b5563;
            margin-bottom: 20px;
            line-height: 1.6;
          ">
            for outstanding efforts in completing the course
          </div>

          <!-- Course name -->
          <div style="
            font-size: 32pt;
            font-weight: 600;
            color: #1f2937;
            margin-bottom: 35px;
            letter-spacing: 1px;
            line-height: 1.4;
          ">
            ${data.courseName}
          </div>

          <div style="
            font-size: 14pt;
            color: #6b7280;
          ">
            Date: ${data.completionDate}
          </div>
        </div>

        <!-- Footer with signatures -->
        <div style="
          display: flex;
          justify-content: space-between;
          align-items: flex-end;
          margin-top: 20px;
          padding-top: 20px;
          border-top: 1px solid #e5e7eb;
        ">
          <!-- Left signature -->
          <div style="flex: 1; text-align: center;">
            <div style="
              width: 140px;
              height: 2px;
              background: #1f2937;
              margin: 0 auto 8px;
            "></div>
            <div style="
              font-size: 12pt;
              color: #1f2937;
              font-weight: 600;
            ">
              ${data.instructorName}
            </div>
            <div style="
              font-size: 11pt;
              color: #6b7280;
              font-style: italic;
              margin-top: 3px;
            ">
              INSTRUCTOR
            </div>
          </div>

          <!-- Center badge -->
          <div style="flex: 1; text-align: center; padding: 0 20px;">
            <div style="
              width: 90px;
              height: 90px;
              background: linear-gradient(135deg, #fbbf24 0%, #f59e0b 100%);
              border-radius: 50%;
              display: flex;
              align-items: center;
              justify-content: center;
              margin: 0 auto 10px;
              box-shadow: 0 12px 28px rgba(251, 191, 36, 0.25);
              position: relative;
            ">
              <div style="
                position: absolute;
                inset: -4px;
                border: 3px solid #f59e0b;
                border-radius: 50%;
              "></div>
              <div style="
                font-size: 48pt;
                z-index: 1;
              ">
                ⭐
              </div>
            </div>
            <div style="
              font-size: 11pt;
              color: #6b7280;
              font-weight: 500;
            ">
              Certificate ID
            </div>
            <div style="
              font-size: 10pt;
              color: #dc2626;
              font-weight: 600;
              letter-spacing: 1px;
              margin-top: 2px;
            ">
              ${data.certificateId}
            </div>
          </div>

          <!-- Right signature -->
          <div style="flex: 1; text-align: center;">
            <div style="
              width: 140px;
              height: 2px;
              background: #1f2937;
              margin: 0 auto 8px;
            "></div>
            <div style="
              font-size: 12pt;
              color: #1f2937;
              font-weight: 600;
            ">
              Sudha A.
            </div>
            <div style="
              font-size: 11pt;
              color: #6b7280;
              font-style: italic;
              margin-top: 3px;
            ">
              FOUNDER & CEO
            </div>
          </div>
        </div>
      </div>
    </div>
  `;

  document.body.appendChild(certificateDiv);

  try {
    // Generate canvas from the certificate
    const canvas = await html2canvas(certificateDiv, {
      width: 1200,
      height: 800,
      scale: 2,
      backgroundColor: '#ffffff',
      useCORS: true
    });

    // Create PDF
    const pdf = new jsPDF({
      orientation: 'landscape',
      unit: 'px',
      format: [1200, 800]
    });

    const imgData = canvas.toDataURL('image/png');
    pdf.addImage(imgData, 'PNG', 0, 0, 1200, 800);

    // Download the PDF
    pdf.save(`Certificate_${data.courseName.replace(/\s+/g, '_')}_${data.studentName.replace(/\s+/g, '_')}.pdf`);

  } catch (error) {
    console.error('Error generating certificate:', error);
    alert('Failed to generate certificate. Please try again.');
  } finally {
    // Clean up
    document.body.removeChild(certificateDiv);
  }
};

export const generateCertificateHTML = (data: CertificateData): string => {
  return `
    <div style="
      width: 100%;
      height: 100%;
      background: linear-gradient(135deg, #ffffff 0%, #f5f0ed 50%, #ffffff 100%);
      position: relative;
      font-family: 'Georgia', 'Garamond', serif;
      display: flex;
      flex-direction: column;
      padding: 0;
      box-sizing: border-box;
      overflow: hidden;
    ">
      <!-- Decorative gradient background shapes -->
      <div style="
        position: absolute;
        top: -200px;
        right: -200px;
        width: 500px;
        height: 500px;
        background: radial-gradient(circle, rgba(220, 38, 38, 0.08) 0%, transparent 70%);
        border-radius: 50%;
      "></div>
      
      <div style="
        position: absolute;
        bottom: -150px;
        left: -150px;
        width: 400px;
        height: 400px;
        background: radial-gradient(circle, rgba(139, 92, 246, 0.08) 0%, transparent 70%);
        border-radius: 50%;
      "></div>

      <!-- Outer decorative border frame -->
      <div style="
        position: absolute;
        top: 30px;
        left: 30px;
        right: 30px;
        bottom: 30px;
        border: 3px solid #d4a574;
        border-radius: 12px;
        pointer-events: none;
      "></div>

      <!-- Inner decorative border -->
      <div style="
        position: absolute;
        top: 40px;
        left: 40px;
        right: 40px;
        bottom: 40px;
        border: 1px solid #d4a574;
        border-radius: 8px;
        pointer-events: none;
      "></div>

      <!-- Certificate ID Top Right -->
      <div style="
        position: absolute;
        top: 55px;
        right: 70px;
        z-index: 20;
        text-align: right;
      ">
        <div style="
          font-size: 11pt;
          color: #6b7280;
          font-weight: 600;
          letter-spacing: 1px;
        ">
          CERT ID
        </div>
        <div style="
          font-size: 9pt;
          color: #4b5563;
          font-family: 'Courier New', monospace;
          margin-top: 3px;
          word-break: break-all;
          max-width: 180px;
        ">
          ${data.certificateId}
        </div>
      </div>

      <!-- Corner decorations -->
      <div style="
        position: absolute;
        top: 50px;
        left: 50px;
        width: 40px;
        height: 40px;
        border-top: 3px solid #dc2626;
        border-left: 3px solid #dc2626;
        border-radius: 6px;
      "></div>
      <div style="
        position: absolute;
        top: 50px;
        right: 50px;
        width: 40px;
        height: 40px;
        border-top: 3px solid #dc2626;
        border-right: 3px solid #dc2626;
        border-radius: 6px;
      "></div>
      <div style="
        position: absolute;
        bottom: 50px;
        left: 50px;
        width: 40px;
        height: 40px;
        border-bottom: 3px solid #dc2626;
        border-left: 3px solid #dc2626;
        border-radius: 6px;
      "></div>
      <div style="
        position: absolute;
        bottom: 50px;
        right: 50px;
        width: 40px;
        height: 40px;
        border-bottom: 3px solid #dc2626;
        border-right: 3px solid #dc2626;
        border-radius: 6px;
      "></div>

      <!-- Main content -->
      <div style="
        flex: 1;
        display: flex;
        flex-direction: column;
        padding: 80px 100px;
        position: relative;
        z-index: 10;
        text-align: center;
      ">
        <!-- Logo and Academy name -->
        <div style="margin-bottom: 30px;">
          <img src="/logo.jpeg" alt="Academy Logo" style="
            width: 70px;
            height: 70px;
            border-radius: 50%;
            margin: 0 auto 15px;
            display: block;
            box-shadow: 0 8px 24px rgba(220, 38, 38, 0.2);
            object-fit: contain;
            background: white;
            padding: 5px;
          " />
          <div style="
            font-size: 28pt;
            font-weight: 700;
            color: #1f2937;
            letter-spacing: 2px;
            margin-bottom: 5px;
          ">
            SUDHA'S ACADEMY
          </div>
          <div style="
            width: 200px;
            height: 2px;
            background: linear-gradient(90deg, transparent, #dc2626, transparent);
            margin: 0 auto 10px;
          "></div>
          <div style="
            font-size: 14pt;
            color: #6b7280;
            letter-spacing: 3px;
            text-transform: uppercase;
            font-weight: 500;
          ">
            Certificate of Achievement
          </div>
        </div>

        <!-- Main certificate text -->
        <div style="flex: 1; display: flex; flex-direction: column; justify-content: center; margin-bottom: 30px;">
          <div style="
            font-size: 16pt;
            color: #4b5563;
            margin-bottom: 25px;
            font-style: italic;
            letter-spacing: 1px;
          ">
            This certificate proudly presented to
          </div>

          <!-- Student name -->
          <div style="
            font-size: 48pt;
            font-weight: 700;
            color: #dc2626;
            margin-bottom: 35px;
            letter-spacing: 2px;
            text-transform: uppercase;
          ">
            ${data.studentName}
          </div>

          <div style="
            width: 400px;
            height: 2px;
            background: #dc2626;
            margin: 0 auto 35px;
          "></div>

          <div style="
            font-size: 15pt;
            color: #4b5563;
            margin-bottom: 20px;
            line-height: 1.6;
          ">
            for outstanding efforts in completing the course
          </div>

          <!-- Course name -->
          <div style="
            font-size: 32pt;
            font-weight: 600;
            color: #1f2937;
            margin-bottom: 35px;
            letter-spacing: 1px;
            line-height: 1.4;
          ">
            ${data.courseName}
          </div>

          <div style="
            font-size: 14pt;
            color: #6b7280;
          ">
            Date: ${data.completionDate}
          </div>
        </div>

        <!-- Footer with signatures -->
        <div style="
          display: flex;
          justify-content: space-between;
          align-items: flex-end;
          margin-top: 20px;
          padding-top: 20px;
          border-top: 1px solid #e5e7eb;
        ">
          <!-- Left signature -->
          <div style="flex: 1; text-align: center;">
            <div style="
              width: 140px;
              height: 2px;
              background: #1f2937;
              margin: 0 auto 8px;
            "></div>
            <div style="
              font-size: 12pt;
              color: #1f2937;
              font-weight: 600;
            ">
              ${data.instructorName}
            </div>
            <div style="
              font-size: 11pt;
              color: #6b7280;
              font-style: italic;
              margin-top: 3px;
            ">
              INSTRUCTOR
            </div>
          </div>

          <!-- Center badge -->
          <div style="flex: 1; text-align: center; padding: 0 20px;">
            <div style="
              width: 90px;
              height: 90px;
              background: linear-gradient(135deg, #fbbf24 0%, #f59e0b 100%);
              border-radius: 50%;
              display: flex;
              align-items: center;
              justify-content: center;
              margin: 0 auto 10px;
              box-shadow: 0 12px 28px rgba(251, 191, 36, 0.25);
              position: relative;
            ">
              <div style="
                position: absolute;
                inset: -4px;
                border: 3px solid #f59e0b;
                border-radius: 50%;
              "></div>
              <div style="
                font-size: 48pt;
                z-index: 1;
              ">
                ⭐
              </div>
            </div>
            <div style="
              font-size: 11pt;
              color: #6b7280;
              font-weight: 500;
            ">
              Certificate ID
            </div>
            <div style="
              font-size: 10pt;
              color: #dc2626;
              font-weight: 600;
              letter-spacing: 1px;
              margin-top: 2px;
            ">
              ${data.certificateId}
            </div>
          </div>

          <!-- Right signature -->
          <div style="flex: 1; text-align: center;">
            <div style="
              width: 140px;
              height: 2px;
              background: #1f2937;
              margin: 0 auto 8px;
            "></div>
            <div style="
              font-size: 12pt;
              color: #1f2937;
              font-weight: 600;
            ">
              Sudha A.
            </div>
            <div style="
              font-size: 11pt;
              color: #6b7280;
              font-style: italic;
              margin-top: 3px;
            ">
              FOUNDER & CEO
            </div>
          </div>
        </div>
      </div>
    </div>
  `;
};
